import { cn } from "../lib/utils.js";
import { getVisualViewportLayerStyle, useVisualViewport } from "../hooks/use-visual-viewport.js";
import { ignorePlacesAutocompletePointerDown } from "../lib/places-autocomplete.js";
import { Button } from "./button.js";
import { DialogOverlay, DialogPortal } from "./dialog.js";
import { X } from "@phosphor-icons/react";
import { jsx, jsxs } from "react/jsx-runtime";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
//#region src/components/action-modal.tsx
const actionModalVariants = cva(cn("tw tw:fixed tw:z-50 tw:flex tw:flex-col tw:overflow-hidden tw:bg-white", "tw:inset-0 tw:max-md:top-[var(--action-modal-viewport-top,0px)] tw:max-md:h-[var(--action-modal-viewport-height,100dvh)]! tw:w-full", "tw:max-md:pt-[env(safe-area-inset-top,0px)] tw:max-md:pb-[env(safe-area-inset-bottom,0px)]", "tw:max-md:pl-[env(safe-area-inset-left,0px)] tw:max-md:pr-[env(safe-area-inset-right,0px)]", "tw:md:inset-auto tw:md:top-1/2 tw:md:left-1/2 tw:md:h-auto tw:md:max-h-[calc(100dvh-4rem)] tw:md:w-[calc(100%-2rem)] tw:md:-translate-x-1/2 tw:md:-translate-y-1/2 tw:md:rounded-sm tw:md:border tw:md:border-stroke tw:md:shadow-lg"), {
	variants: { size: {
		sm: "tw:md:max-w-100",
		md: "tw:md:max-w-125",
		lg: "tw:md:max-w-200",
		xl: "tw:md:max-w-300"
	} },
	defaultVariants: { size: "sm" }
});
function ActionModal({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Root, {
		"data-slot": "action-modal",
		...props
	});
}
function ActionModalTrigger({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Trigger, {
		"data-slot": "action-modal-trigger",
		...props
	});
}
function ActionModalClose({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Close, {
		"data-slot": "action-modal-close",
		...props
	});
}
function ActionModalContent({ className, overlayClassName, size, style, children, ...props }) {
	const viewportLayerStyle = getVisualViewportLayerStyle(useVisualViewport());
	return /* @__PURE__ */ jsxs(DialogPortal, {
		"data-slot": "action-modal-portal",
		children: [/* @__PURE__ */ jsx(DialogOverlay, { className: overlayClassName }), /* @__PURE__ */ jsx(DialogPrimitive.Content, {
			"data-slot": "action-modal-content",
			"aria-describedby": void 0,
			className: cn(actionModalVariants({ size }), className),
			style: {
				...viewportLayerStyle && {
					"--action-modal-viewport-top": `${viewportLayerStyle.top}px`,
					"--action-modal-viewport-height": `${viewportLayerStyle.height}px`
				},
				...style
			},
			...props,
			onPointerDownOutside: ignorePlacesAutocompletePointerDown(props.onPointerDownOutside),
			children
		})]
	});
}
function ActionModalHeader({ className, hideClose = false, children, ...props }) {
	return /* @__PURE__ */ jsxs("div", {
		"data-slot": "action-modal-header",
		className: cn("tw:flex tw:shrink-0 tw:items-center tw:gap-3 tw:border-b tw:border-stroke tw:px-5 tw:py-4.5", className),
		...props,
		children: [children, !hideClose && /* @__PURE__ */ jsx(DialogPrimitive.Close, {
			asChild: true,
			children: /* @__PURE__ */ jsxs(Button, {
				variant: "ghost",
				size: "icon",
				className: "tw:-my-1.5 tw:ml-auto tw:shrink-0",
				children: [/* @__PURE__ */ jsx(X, { className: "tw:h-4 tw:w-4" }), /* @__PURE__ */ jsx("span", {
					className: "tw:sr-only",
					children: "Close"
				})]
			})
		})]
	});
}
function ActionModalTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Title, {
		"data-slot": "action-modal-title",
		className: cn("tw:min-w-0 tw:truncate tw:font-denim tw:text-lg tw:leading-tight tw:font-medium tw:text-midnight", className),
		...props
	});
}
function ActionModalDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Description, {
		"data-slot": "action-modal-description",
		className: cn("tw:min-w-0 tw:truncate tw:text-sm tw:text-midnight/70", className),
		...props
	});
}
function ActionModalBody({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "action-modal-body",
		className: cn("tw:min-h-0 tw:flex-1 tw:overflow-x-hidden tw:overflow-y-auto tw:overscroll-x-none tw:overscroll-y-contain tw:bg-off-white tw:px-5 tw:py-4", className),
		...props
	});
}
function ActionModalFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "action-modal-footer",
		className: cn("tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:justify-end tw:gap-2 tw:border-t tw:border-stroke tw:px-5 tw:py-4", className),
		...props
	});
}
//#endregion
export { ActionModal, ActionModalBody, ActionModalClose, ActionModalContent, ActionModalDescription, ActionModalFooter, ActionModalHeader, ActionModalTitle, ActionModalTrigger, actionModalVariants };

//# sourceMappingURL=action-modal.js.map